"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  commonSpeeds,
  type RadiusUnit,
  rcfConstant,
  rcfFromRpm,
  roundRpm,
  rpmFromRcf,
} from "@/lib/tool-kit/biology-lab/centrifuge";
import {
  type NumberFormatter,
  plain,
  sig,
  toTsv,
} from "@/lib/tool-kit/biology-lab/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const CENTRIFUGE_RCF_CALCULATOR_STRINGS = {
  modeLabel: "Convert",
  modeRpmToRcf: "RPM to RCF (× g)",
  modeRcfToRpm: "RCF (× g) to RPM",
  rpm: "Speed (rpm)",
  rcf: "Relative centrifugal force (× g)",
  radius: "Rotor radius",
  radiusHint:
    "Maximum radius (rmax) from the rotor manual, to the bottom of the tube.",
  radiusUnit: "Radius unit",
  unitCm: "cm",
  unitMm: "mm",
  unitIn: "inches",
  surface: "Radius at the liquid surface (optional)",
  surfaceHint:
    "Distance from the axis to the top of the liquid; shows the lower end of the range.",
  results: "Results",
  rcfResult: "RCF at the bottom of the tube",
  rcfSurface: "RCF at the liquid surface",
  rpmResult: "Set the centrifuge to",
  unitG: "× g",
  unitRpm: "rpm",
  closest: "Closest {step} rpm setting",
  closestRcf: "gives {value} × g",
  working: "The working",
  workingRcf: "RCF = {k} × r × rpm² = {k} × {r} × {rpm}² = {result} × g",
  workingRpm: "rpm = √(RCF ÷ ({k} × r)) = √({rcf} ÷ ({k} × {r})) = {result}",
  constantNote:
    "The constant {k} applies to a radius in {unit}: it is (2π ÷ 60)² ÷ g with g = 980.665 cm/s², rounded to four digits.",
  tableHeading: "Common speeds for this rotor",
  colRcf: "Target (× g)",
  colRpm: "Speed (rpm)",
  copyResult: "Copy result",
  copyTable: "Copy table",
  copied: "Copied!",
  copySummary: "{rpm} rpm = {rcf} × g (r = {r} {unit})",
  errRpm: "Enter a speed greater than 0 rpm.",
  errRcf: "Enter a force greater than 0 × g.",
  errRadius: "Enter a radius greater than 0.",
  errSurface:
    "The surface radius must be greater than 0 and smaller than the rotor radius.",
  enter: "Enter the speed and the rotor radius.",
  enterRcf: "Enter the force and the rotor radius.",
  adviceNote:
    "Rotors and tubes have a maximum rated speed and force. Check the rotor manual before running at a computed setting.",
} as const;

type Strings = typeof CENTRIFUGE_RCF_CALCULATOR_STRINGS;
type Mode = "rpm" | "rcf";

const ID = "rcf";
const ROUND_STEP = 10;
const BIG_RCF = 100;

function unitLabel(unit: RadiusUnit, strings: Strings): string {
  const labels: Record<RadiusUnit, string> = {
    cm: strings.unitCm,
    mm: strings.unitMm,
    in: strings.unitIn,
  };
  return labels[unit];
}

/** × g to three or four significant figures — whole numbers once past 100. */
function force(fmt: NumberFormatter, value: number): string {
  return value >= BIG_RCF
    ? fmt(value, { maximumFractionDigits: 0 })
    : fmt(value, { maximumSignificantDigits: 3 });
}

function whole(fmt: NumberFormatter, value: number): string {
  return fmt(value, { maximumFractionDigits: 0 });
}

function SpeedTable({
  radius,
  unit,
  fmt,
  strings,
}: {
  radius: number;
  unit: RadiusUnit;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const head = [strings.colRcf, strings.colRpm];
  const rows = commonSpeeds(radius, unit).map((row) => [
    plain(fmt, row.rcf),
    whole(fmt, row.rpm),
  ]);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable head={head} rows={rows} />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyTable}
        small
        text={toTsv(head, rows)}
      />
    </div>
  );
}

function RpmToRcf({
  rpm,
  radius,
  surface,
  unit,
  fmt,
  strings,
}: {
  rpm: number;
  radius: number;
  surface: number | null;
  unit: RadiusUnit;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const rcf = rcfFromRpm(rpm, radius, unit);
  const k = sig(fmt, rcfConstant(unit), 4);
  const atSurface = surface === null ? null : rcfFromRpm(rpm, surface, unit);
  const summary = t(strings.copySummary, {
    rpm: plain(fmt, rpm),
    rcf: force(fmt, rcf),
    r: plain(fmt, radius),
    unit: unitLabel(unit, strings),
  });
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={`${strings.rcfResult} (${strings.unitG})`}
          value={force(fmt, rcf)}
        />
        {atSurface === null ? null : (
          <StatCard
            label={`${strings.rcfSurface} (${strings.unitG})`}
            value={force(fmt, atSurface)}
          />
        )}
      </StatGrid>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {t(strings.workingRcf, {
            k,
            r: plain(fmt, radius),
            rpm: plain(fmt, rpm),
            result: force(fmt, rcf),
          })}
        </p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
    </>
  );
}

function RcfToRpm({
  rcf,
  radius,
  unit,
  fmt,
  strings,
}: {
  rcf: number;
  radius: number;
  unit: RadiusUnit;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const rpm = rpmFromRcf(rcf, radius, unit);
  const rounded = roundRpm(rpm, ROUND_STEP);
  const k = sig(fmt, rcfConstant(unit), 4);
  const summary = t(strings.copySummary, {
    rpm: whole(fmt, rpm),
    rcf: force(fmt, rcf),
    r: plain(fmt, radius),
    unit: unitLabel(unit, strings),
  });
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={`${strings.rpmResult} (${strings.unitRpm})`}
          value={whole(fmt, rpm)}
        />
        <StatCard
          label={`${t(strings.closest, { step: plain(fmt, ROUND_STEP) })} (${strings.unitRpm})`}
          value={whole(fmt, rounded)}
        />
      </StatGrid>
      <p className="text-muted-foreground text-sm">
        {whole(fmt, rounded)} {strings.unitRpm}{" "}
        {t(strings.closestRcf, {
          value: force(fmt, rcfFromRpm(rounded, radius, unit)),
        })}
      </p>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {t(strings.workingRpm, {
            k,
            rcf: plain(fmt, rcf),
            r: plain(fmt, radius),
            result: whole(fmt, rpm),
          })}
        </p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
    </>
  );
}

export default function CentrifugeRcfCalculatorWidget() {
  const strings = useToolUi(CENTRIFUGE_RCF_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("rpm");
  const [rpm, setRpm] = useState("14000");
  const [rcf, setRcf] = useState("1000");
  const [radius, setRadius] = useState("8.5");
  const [unit, setUnit] = useState<RadiusUnit>("cm");
  const [surface, setSurface] = useState("");

  const radiusValue = parseMeasuredNumber(radius);
  const rpmValue = parseMeasuredNumber(rpm);
  const rcfValue = parseMeasuredNumber(rcf);
  const surfaceValue = parseMeasuredNumber(surface);

  function results() {
    if (radiusValue === null) {
      return (
        <Notice>{mode === "rpm" ? strings.enter : strings.enterRcf}</Notice>
      );
    }
    if (!(radiusValue > 0)) {
      return <ErrorNotice>{strings.errRadius}</ErrorNotice>;
    }
    return mode === "rpm" ? rpmBranch(radiusValue) : rcfBranch(radiusValue);
  }

  function rpmBranch(radiusOk: number) {
    if (rpmValue === null) {
      return <Notice>{strings.enter}</Notice>;
    }
    if (!(rpmValue > 0)) {
      return <ErrorNotice>{strings.errRpm}</ErrorNotice>;
    }
    if (
      surfaceValue !== null &&
      !(surfaceValue > 0 && surfaceValue < radiusOk)
    ) {
      return <ErrorNotice>{strings.errSurface}</ErrorNotice>;
    }
    return (
      <RpmToRcf
        fmt={fmt}
        radius={radiusOk}
        rpm={rpmValue}
        strings={strings}
        surface={surfaceValue}
        unit={unit}
      />
    );
  }

  function rcfBranch(radiusOk: number) {
    if (rcfValue === null) {
      return <Notice>{strings.enterRcf}</Notice>;
    }
    if (!(rcfValue > 0)) {
      return <ErrorNotice>{strings.errRcf}</ErrorNotice>;
    }
    return (
      <RcfToRpm
        fmt={fmt}
        radius={radiusOk}
        rcf={rcfValue}
        strings={strings}
        unit={unit}
      />
    );
  }

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name={`${ID}-mode`}
        onChange={setMode}
        options={[
          { value: "rpm", label: strings.modeRpmToRcf },
          { value: "rcf", label: strings.modeRcfToRpm },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {mode === "rpm" ? (
          <TextField
            id={`${ID}-rpm`}
            label={strings.rpm}
            numeric
            onChange={setRpm}
            value={rpm}
          />
        ) : (
          <TextField
            id={`${ID}-rcf`}
            label={strings.rcf}
            numeric
            onChange={setRcf}
            value={rcf}
          />
        )}
        <TextField
          hint={strings.radiusHint}
          id={`${ID}-radius`}
          label={strings.radius}
          numeric
          onChange={setRadius}
          value={radius}
        />
        <SelectField
          id={`${ID}-unit`}
          label={strings.radiusUnit}
          onChange={setUnit}
          options={[
            { value: "cm", label: strings.unitCm },
            { value: "mm", label: strings.unitMm },
            { value: "in", label: strings.unitIn },
          ]}
          value={unit}
        />
        {mode === "rpm" ? (
          <TextField
            hint={strings.surfaceHint}
            id={`${ID}-surface`}
            label={strings.surface}
            numeric
            onChange={setSurface}
            value={surface}
          />
        ) : null}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <div className={`${RESULT_CARD} space-y-4`}>{results()}</div>
        {radiusValue !== null && radiusValue > 0 ? (
          <SpeedTable
            fmt={fmt}
            radius={radiusValue}
            strings={strings}
            unit={unit}
          />
        ) : null}
        <p className="text-muted-foreground text-xs">
          {t(strings.constantNote, {
            k: sig(fmt, rcfConstant(unit), 4),
            unit: unitLabel(unit, strings),
          })}{" "}
          {strings.adviceNote}
        </p>
      </section>
    </>
  );
}
